/**
 * Customer-side auth context (parallel to PanelAuthContext).
 * Stores JWT in localStorage. Exposes signup / login / forgot / reset / logout / track helpers.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/customers`;
const TOKEN_KEY = 'entorid_customer_token';

const CustomerAuthContext = createContext(null);

export function formatApiError(err) {
  const r = err?.response;
  if (!r) return err?.message || 'Network error — please check your connection and try again';
  const d = r.data;
  if (typeof d === 'string' && d.length < 200) return d;
  if (d?.detail) return typeof d.detail === 'string' ? d.detail : JSON.stringify(d.detail);
  // Friendlier mapping for common HTTP codes
  if (r.status === 404) return 'Service temporarily unavailable. Please try again in a moment.';
  if (r.status === 429) return 'Too many attempts. Please wait a minute and try again.';
  if (r.status === 502 || r.status === 503) return 'Server is busy. Please try again in a moment.';
  return r.statusText || `Error ${r.status}`;
}

export function CustomerAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [customer, setCustomer] = useState(null);
  const [loadingMe, setLoadingMe] = useState(false);

  // Bootstrap: if token present, fetch /me
  useEffect(() => {
    let alive = true;
    if (!token) { setCustomer(null); return undefined; }
    setLoadingMe(true);
    axios.get(`${API_BASE}/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (alive) setCustomer(r.data); })
      .catch(() => {
        // Invalid/expired — purge
        localStorage.removeItem(TOKEN_KEY);
        if (alive) { setToken(''); setCustomer(null); }
      })
      .finally(() => { if (alive) setLoadingMe(false); });
    return () => { alive = false; };
  }, [token]);

  const signup = useCallback(async (payload) => {
    const r = await axios.post(`${API_BASE}/signup`, payload);
    localStorage.setItem(TOKEN_KEY, r.data.token);
    setToken(r.data.token);
    setCustomer(r.data.customer);
    return r.data;
  }, []);

  const login = useCallback(async (identifier, password) => {
    const r = await axios.post(`${API_BASE}/login`, { identifier, password });
    localStorage.setItem(TOKEN_KEY, r.data.token);
    setToken(r.data.token);
    setCustomer(r.data.customer);
    return r.data;
  }, []);

  // Emergent-managed Google sign-in — kick off (redirect to hosted Google screen).
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
  // THIS BREAKS THE AUTH.
  const startGoogleSignIn = useCallback(() => {
    const redirectUrl = window.location.origin + '/account';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  // Exchange the session_id (from OAuth redirect hash) for our native JWT.
  const exchangeGoogleSession = useCallback(async (sessionId) => {
    const r = await axios.post(`${API_BASE}/google/session`, null, {
      headers: { 'X-Session-ID': sessionId },
    });
    localStorage.setItem(TOKEN_KEY, r.data.token);
    setToken(r.data.token);
    setCustomer(r.data.customer);
    return r.data; // { token, customer, is_new, needs_mobile }
  }, []);

  const completeMobile = useCallback(async ({ mobile, city }) => {
    const r = await axios.post(`${API_BASE}/me/complete-mobile`, { mobile, city }, {
      headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ''}` },
    });
    setCustomer(r.data);
    return r.data;
  }, []);

  const forgot = useCallback(async (email) => {
    const r = await axios.post(`${API_BASE}/forgot-password`, { email });
    return r.data;
  }, []);

  const reset = useCallback(async (email, otp, new_password) => {
    const r = await axios.post(`${API_BASE}/reset-password`, { email, otp, new_password });
    return r.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setCustomer(null);
  }, []);

  const track = useCallback((event_type, payload = {}, path) => {
    // Fire-and-forget; works for anonymous (no auth) AND logged-in customers
    try {
      const url = `${API_BASE}/track`;
      const headers = { 'Content-Type': 'application/json' };
      const t = localStorage.getItem(TOKEN_KEY);
      if (t) headers.Authorization = `Bearer ${t}`;
      // Use sendBeacon-friendly axios.post but don't await
      axios.post(url, {
        event_type, payload,
        path: path ?? window.location.pathname,
        referrer: document.referrer || null,
      }, { headers }).catch(() => {});
    } catch (e) {
      // swallow
    }
  }, []);

  const updateProfile = useCallback(async (patch) => {
    const r = await axios.patch(`${API_BASE}/me`, patch, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setCustomer(r.data);
    return r.data;
  }, [token]);

  const value = useMemo(() => ({
    token, customer, loadingMe, isLoggedIn: !!customer,
    signup, login, logout, forgot, reset, track, updateProfile,
    startGoogleSignIn, exchangeGoogleSession, completeMobile,
  }), [token, customer, loadingMe, signup, login, logout, forgot, reset, track, updateProfile, startGoogleSignIn, exchangeGoogleSession, completeMobile]);

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
}

export function useCustomerAuth() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error('useCustomerAuth must be used inside CustomerAuthProvider');
  return ctx;
}
